/* ADDING A NOTE, FROM ANYWHERE ON THE RECORD                          (PRD 6)
 *
 * "Adding a note is a persistent action on the record, not something reached by
 * first finding the right tab. It sits with the header actions and is available
 * from whichever tab is open, because the moment somebody learns that his daughter
 * has started at the club is not a moment they will navigate for."
 *
 * It used to live inside a Notes tab, which is exactly the shape that sentence
 * argues against: a fact you heard on the floor had to survive you walking to a
 * screen and picking the right tab before it got written down.
 *
 * THE NOTE IS AN EVENT AND HAS ONE HOME. It appends to the timeline with its author
 * and its time and is stored nowhere else, so there is one copy of the sentence.
 */
import { useState } from 'react';
import type { Member } from '@acrostic/model';
import { Field, Modal } from '../../components/Modal.js';
import { useSource } from '../../data/SourceProvider.js';
import { useToast } from '../../components/Toast.js';

export function AddNote({ m, onClose, onDone }: {
  m: Member; onClose: () => void; onDone: () => void;
}) {
  const source = useSource();
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try {
      await source.addMemberNote(m.id, t);
      toast(`Noted on ${m.firstName}'s record`, 'good');
      onDone();
    } catch (e) {
      toast((e as Error).message, 'bad');
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Note on ${m.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn t-label-medium" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary t-label-medium"
            disabled={!text.trim() || busy} onClick={() => void save()}>
            Write it down
          </button>
        </>
      }
    >
      <Field
        label="What did you learn"
        hint="Human facts belong here: brings his own coffee, cycles in, marathon in September. Only what they volunteered and would be comfortable seeing."
      >
        <textarea
          className="field" rows={3} value={text} autoFocus
          placeholder={`Something worth remembering about ${m.firstName}.`}
          onChange={(e) => setText(e.target.value)}
        />
      </Field>
      <p className="t-caption muted">
        It lands on the timeline with your name and today&rsquo;s date, and the timeline
        filters down to notes alone for anyone who wants only those.
      </p>
    </Modal>
  );
}
